/**
 * Priority-ordered prefetch for marketing routes (after home has painted).
 * Warm HIW/Claritask images + page chunks first so nav clicks paint immediately.
 */
import {
    CLARITASK_PREFETCH_IMAGES,
    HIW_PREFETCH_IMAGES,
    LANDING_IMAGES,
} from "./landingData";

const warmedImages = new Set();
const HIW_FIRST_IMAGE = LANDING_IMAGES.incomingEmails;

function preloadImageHighPriority(src) {
    if (typeof document === "undefined" || !src || warmedImages.has(`preload:${src}`)) {
        return;
    }
    warmedImages.add(`preload:${src}`);
    warmedImages.add(src);

    const existing = document.querySelector(`link[data-clk-preload-img="${src}"]`);
    if (existing) {
        return;
    }

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.type = "image/webp";
    link.setAttribute("fetchpriority", "high");
    link.setAttribute("data-clk-preload-img", src);
    document.head.appendChild(link);

    // Also decode into memory so the <img> can paint from cache immediately
    const img = new Image();
    img.decoding = "sync";
    img.fetchPriority = "high";
    img.src = src;
}

export function prefetchImages(urls) {
    if (typeof window === "undefined" || !urls?.length) {
        return;
    }
    urls.forEach((src) => {
        if (!src || warmedImages.has(src)) {
            return;
        }
        warmedImages.add(src);
        const img = new Image();
        img.decoding = "async";
        img.src = src;
    });
}

export function prefetchHiwAndClaritaskImages() {
    preloadImageHighPriority(HIW_FIRST_IMAGE);
    prefetchImages([
        ...HIW_PREFETCH_IMAGES.filter((src) => src !== HIW_FIRST_IMAGE),
        ...CLARITASK_PREFETCH_IMAGES,
    ]);
}

/**
 * How it works / Claritask page JS is prefetched via webpackPrefetch magic
 * comments on their lazy() imports (see LandingApp.js) — this only warms images.
 */
export function prefetchHiwAndClaritaskPages() {
    prefetchHiwAndClaritaskImages();
    return Promise.resolve();
}

export function prefetchAppBundle({ priority = "prefetch" } = {}) {
    const href = typeof window !== "undefined" ? window.__CLARITICK_APP_JS__ : null;
    if (!href || document.querySelector("link[data-clk-prefetch-app]")) {
        return Promise.resolve();
    }

    const rel = priority === "preload" ? "preload" : "prefetch";

    return new Promise((resolve) => {
        const link = document.createElement("link");
        link.rel = rel;
        link.as = "script";
        link.href = href;
        link.setAttribute("data-clk-prefetch-app", "1");
        link.onload = () => resolve();
        link.onerror = () => resolve();
        document.head.appendChild(link);
        // prefetch may not fire onload in all browsers
        window.setTimeout(resolve, priority === "preload" ? 8000 : 4000);
    });
}

/** Warm HIW/Claritask images immediately; then prefetch app.js in the background. */
export function runLandingPrefetchPipeline() {
    // Highest priority: first HIW screenshot
    prefetchHiwAndClaritaskImages();

    const schedule = (fn) => {
        if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(fn, { timeout: 2000 });
        } else {
            window.setTimeout(fn, 200);
        }
    };

    schedule(() => {
        // Signed-in visitors on marketing pages are likely heading to the app — preload app.js.
        const hasToken = typeof document !== "undefined"
            && document.cookie.split(";").some((c) => c.trim().startsWith("access_token="));
        prefetchAppBundle({ priority: hasToken ? "preload" : "prefetch" });
    });
}
